// Utilities for handling Google Drive shared URLs.
// Drive blocks hotlinking of /file/d/<id>/view URLs in <img> and <video> tags.
// We must convert them to either /thumbnail (for images) or /preview (for videos/pdfs in iframe).

/**
 * Extract a Google Drive file ID from any of the common share URL formats:
 *  - https://drive.google.com/file/d/FILE_ID/view?usp=sharing
 *  - https://drive.google.com/file/d/FILE_ID/preview
 *  - https://drive.google.com/open?id=FILE_ID
 *  - https://docs.google.com/uc?id=FILE_ID&export=download
 * Returns null if not a Drive URL.
 */
export function extractDriveId(url) {
  if (!url || typeof url !== 'string') return null;
  // /file/d/<id>/...
  let m = url.match(/\/file\/d\/([a-zA-Z0-9_-]{20,})/);
  if (m) return m[1];
  // open?id=<id>  or  uc?id=<id>
  m = url.match(/[?&]id=([a-zA-Z0-9_-]{20,})/);
  if (m) return m[1];
  // /d/<id>/
  m = url.match(/drive\.google\.com\/.*?\/d\/([a-zA-Z0-9_-]{20,})/);
  if (m) return m[1];
  return null;
}

export function isDriveUrl(url) {
  if (!url) return false;
  return /drive\.google\.com|docs\.google\.com/i.test(url);
}

/**
 * Convert any URL to an embeddable iframe URL.
 * For Drive: /preview format that works inside iframes.
 * For YouTube: returns embed URL.
 * Otherwise returns the original URL.
 */
export function toEmbedUrl(url) {
  if (!url) return url;
  const id = extractDriveId(url);
  if (id) return `https://drive.google.com/file/d/${id}/preview`;
  // YouTube
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  return url;
}

/**
 * Convert any URL to a thumbnail URL usable in <img src>.
 * For Drive: uses the official thumbnail endpoint (works with public/anyone-with-link files).
 * Otherwise returns the original URL.
 *
 * @param {string} url The original URL
 * @param {number} width Desired thumbnail width in pixels (default 400)
 */
export function toThumbnailUrl(url, width = 400) {
  if (!url) return url;
  const id = extractDriveId(url);
  if (id) {
    return `https://drive.google.com/thumbnail?id=${id}&sz=w${width}`;
  }
  return url;
}

/**
 * Returns true if a URL points to a video file we can play directly with <video>
 * (i.e., NOT Drive/YouTube, but a direct CDN or .mp4 link).
 */
export function isDirectVideo(url) {
  if (!url) return false;
  if (isDriveUrl(url)) return false;
  if (/youtube\.com|youtu\.be|vimeo\.com/i.test(url)) return false;
  return /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(url) || true; // assume direct unless known platform
}
